import { NextPageHead } from '@aspendental/shared-components-web';
import { PageThemeContextProvider } from '@aspendental/shared-components-web/lib/context/PageThemeProvider.ctx';
import { useContentfulLiveUpdates } from '@contentful/live-preview/react';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import { useEffect } from 'react';
import choosePreview from './utils/choose-preview';
import getConfigByBrand from './utils/get-config-by-brand';
import getThemeByDomainName from './utils/get-theme-by-domain-name';
import { SchedulingPage } from '@src/components/scheduling/SchedulingPage';
import { MAX_BOOKING_ATTEMPTS } from '@src/constants';
import { AppConfigByAppNameDocument, AppointmentBookingPage, AppointmentBookingPageByPathDocument, PageContentsItem, RichTextByIdDocument } from '@src/lib/__generated/sdk';
import { getClient } from '@src/lib/client';
import { getLogger } from '@src/lib/logging/logger';
import DetermineIndexing from '@src/pages/utils/determine-indexing';
import { getServerSideTranslations } from '@src/pages/utils/get-serverside-translations';
import { checkUserSession } from '@src/services/checkUserSession';
import { getSession } from '@src/services/session';
import { useSchedulingStore } from '@src/store';
const Page = (props: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const pageData = useContentfulLiveUpdates(props.page);
  const {
    name,
    path,
    title,
    indexed,
    description,
    canonical,
    breadcrumbsCollection,
    ogImage
  } = pageData;
  const pageTheme = props.page?.pageTheme || 'Primary';
  const {
    setIsUserFlagged
  } = useSchedulingStore(state => state);
  useEffect(() => {
    import('@src/utils/braze-exports').then(({
      initialize,
      openSession,
      automaticallyShowInAppMessages
    }) => {
      initialize(props.env.BRAZE_SDK_KEY, {
        baseUrl: 'sdk.iad-02.braze.com',
        enableLogging: props.env?.ENVIRONMENT_NAME !== 'prod'
      });
      // Enable inApp Messaging. Refer to https://www.braze.com/docs/developer_guide/platform_integration_guides/web/initial_sdk_setup/#step-1-install-the-braze-library
      automaticallyShowInAppMessages();
      openSession();
    });
  }, [props.env?.BRAZE_SDK_KEY, props.env]);
  useEffect(() => {
    setIsUserFlagged(props.isUserFlagged);
  }, [props.isUserFlagged, setIsUserFlagged]);
  return <>
			<PageThemeContextProvider pageTheme={pageTheme} data-sentry-element="PageThemeContextProvider" data-sentry-source-file="[...slug].page.tsx">
				<NextPageHead name={name} path={path} title={title} indexed={DetermineIndexing(indexed, props.env)} description={description} canonical={canonical} breadcrumbs={breadcrumbsCollection?.items} originUrl={process.env.NEXT_PUBLIC_SITE_URL} ogImage={ogImage} data-sentry-element="NextPageHead" data-sentry-source-file="[...slug].page.tsx" />
				<SchedulingPage featuredContent={pageData.featuredContent} bottomContentCollection={pageData.bottomContentCollection} data-sentry-element="SchedulingPage" data-sentry-source-file="[...slug].page.tsx" />
			</PageThemeContextProvider>
		</>;
};
export const getServerSideProps: GetServerSideProps = async ({
  params,
  locale,
  req,
  res,
  preview = false
}) => {
  const logger = getLogger('landing-page');
  if (!params?.slug) {
    return {
      notFound: true
    };
  }
  try {
    const domain = req.headers.host;
    const theme = getThemeByDomainName(domain);
    const headerTagTheme = Array.isArray(req.headers['x-tag-theme']) ? (req.headers['x-tag-theme'] as TagThemeType[])[0] : req.headers['x-tag-theme'] as TagThemeType;
    // Priority: HTTP header x-tag-theme > domain > process.env.TAG_BRAND_THEME
    const tagTheme = headerTagTheme || theme || process.env.TAG_BRAND_THEME || 'aspendental';
    const brandConfig = getConfigByBrand(tagTheme);
    preview = choosePreview(req.headers.host, preview);
    const appName = brandConfig.contentfulAppId || process.env.TAG_APP_NAME || 'TAG_Commercial_Web_Sandbox';
    const gqlClient = await getClient(preview);
    if (gqlClient === null) {
      logger.error('GQL_CLIENT_IS_NULL: missing token for fetching contentful data');
      throw new Error('gqlClient is null');
    }
    const appConfig = await gqlClient.query(AppConfigByAppNameDocument, {
      appName,
      preview
    }).toPromise();
    const appData = appConfig.data?.appCollection?.items[0];

    // replace [env] with environment name for locationBarFindOfficeUrl
    // TODO: create a brand variable context to replace those variables from CMS in future
    if (appData && appData.header?.locationBarFindOfficeUrl) {
      const brandSubdomain = process.env.ENVIRONMENT_NAME !== 'prod' ? 'wwwstg' : 'www';
      appData.header.locationBarFindOfficeUrl = appData.header?.locationBarFindOfficeUrl?.replace('[env]', brandSubdomain);
    }
    if (!appData) {
      return {
        notFound: true
      };
    }
    const slugs = params.slug as string[];
    const path = '/' + slugs.join('/');
    logger.info(path);
    const contentfulPath = process.env.APP_PATH + path;
    const alternativePath = contentfulPath.endsWith('/') ? contentfulPath : contentfulPath + '/';
    const title = brandConfig.name;
    const pageData = await gqlClient.query(AppointmentBookingPageByPathDocument, {
      path: contentfulPath,
      alternativePath: alternativePath,
      preview,
      title
    }).toPromise();
    const page = pageData.data.appointmentBookingPageCollection?.items[0] as AppointmentBookingPage;
    if (!page) {
      return {
        notFound: true
      };
    }
    // Add links information for rich text rendering
    const pageContent: PageContentsItem[] = [];
    if (page?.bottomContentCollection?.items?.length as number > 0) {
      for (let i = 0; i < page?.bottomContentCollection!.items.length; i++) {
        const item = page?.bottomContentCollection?.items[i] as PageContentsItem;
        if (item?.__typename === 'ContentTypeRichText' && item?.sys?.id) {
          const newRichText = await gqlClient.query(RichTextByIdDocument, {
            id: item?.sys?.id,
            preview
          }).toPromise();
          if (newRichText.data.contentTypeRichText?.content?.links) {
            pageContent.push(newRichText.data.contentTypeRichText);
            continue;
          }
        }
        pageContent.push(item);
      }
    }
    const session = getSession(req, res);
    const isUserFlagged = checkUserSession(req, res, session, MAX_BOOKING_ATTEMPTS);
    return {
      props: {
        isUserFlagged,
        ...(await getServerSideTranslations(locale)),
        env: {
          ENVIRONMENT_NAME: process.env.ENVIRONMENT_NAME,
          BRAZE_SDK_KEY: process.env.BRAZE_SDK_KEY
        },
        config: brandConfig,
        page: {
          ...page,
          contentsCollection: {
            items: pageContent
          }
        },
        appData,
        footerData: appData?.footer
      }
    };
  } catch (e) {
    logger.error(`LANDING_PAGE_LOAD_FAILURE: ${e}`);
    return {
      notFound: true
    };
  }
};
export default Page;